import { Box, Slider, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useErrorHandler } from "../../../common/hooks";
import useDebouncedCallback from "../../../common/hooks/useDebouncedCallback";
import { useComponentSelection } from "../../Hooks/useComponentSelection";
import useObservedExpression from "../../Hooks/useObservedExpression";
import { InputSliderModel, InputSliderValueLabel } from "../../Models/InputSliderModel";
import IComponentProps from "../IComponentProps";
import getComponentLayout from "../Table/getComponentLayout";

const valueLabelMap: Record<InputSliderValueLabel, "auto" | "on" | "off"> = {
  [InputSliderValueLabel.Auto]: "auto",
  [InputSliderValueLabel.On]: "on",
  [InputSliderValueLabel.Off]: "off",
};

const InputSliderComponent: React.FC<IComponentProps<InputSliderModel>> = ({ component, context, componentPath = [] }) => {
  const errorHandler = useErrorHandler();

  const min = component.Min ?? 0;
  const max = component.Max ?? 100;
  const step = component.Step ?? 1;

  const [value, setValue] = useState<number>(min);
  const [valueResult] = useObservedExpression<number>(component.Value, context, "InputSliderComponent");

  useEffect(() => {
    if (valueResult !== undefined && valueResult !== value) {
      setValue(valueResult);
    }
  }, [valueResult]);

  const debouncedUpdateContext = useDebouncedCallback((newValue: number) => {
    if (component.Value) {
      try {
        context.Evaluator.assign(component.Value, context, newValue);
      } catch (error) {
        errorHandler(error);
      }
    }
  }, component.DebunceMs);

  const handleChange = (_event: Event, newValue: number | number[]) => {
    const numeric = Array.isArray(newValue) ? newValue[0] : newValue;
    setValue(numeric);
    debouncedUpdateContext(numeric);
  };

  const componentLayoutSx = getComponentLayout(component);
  const selectionProps = useComponentSelection({ component, componentPath });
  const valueLabelDisplay = valueLabelMap[component.ValueLabel ?? InputSliderValueLabel.Auto];

  return (
    <Box sx={componentLayoutSx} {...selectionProps}>
      {component.Label && (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {component.Label}
        </Typography>
      )}
      <Slider value={value} onChange={handleChange} min={min} max={max} step={step} marks={component.ShowMarks} valueLabelDisplay={valueLabelDisplay} />
    </Box>
  );
};

export default InputSliderComponent;
